import type { NlsKey } from '../../../nls/strings_nls'
import { toLocalIsoDate } from '../../../utils/localDateTimeInput'

export type BookingScreenAction =
  | 'book'
  | 'cancel'
  | 'move'
  | 'leaveWaitlist'
  | 'checkIn'
  | 'link'

export interface FormatBookingErrorOptions {
  action?: BookingScreenAction
  blockedUntil?: number | string | null
  workspaceName?: string
}

export function formatMemberBookingError(
  err: unknown,
  t: (key: NlsKey, placeholders?: Record<string, string | number>) => string,
  options?: FormatBookingErrorOptions,
): string {
  const code =
    typeof err === 'object' && err && 'code' in err
      ? String((err as { code: unknown }).code).replace(/^functions\//, '')
      : ''

  const details =
    typeof err === 'object' && err && 'details' in err
      ? (err as { details: unknown }).details
      : undefined

  const detailsBlocked =
    typeof details === 'object' && details && 'blockedUntil' in details
      ? (details as { blockedUntil?: unknown }).blockedUntil
      : undefined

  const hasDetailsBlocked = detailsBlocked != null && detailsBlocked !== ''

  let hasFutureCustomerBlocked = false
  if (options?.blockedUntil != null && options.blockedUntil !== '') {
    const raw = options.blockedUntil
    const ts =
      typeof raw === 'number'
        ? raw
        : typeof raw === 'string'
          ? !isNaN(Number(raw))
            ? Number(raw)
            : Date.parse(raw)
          : NaN
    if (!isNaN(ts) && ts > Date.now()) {
      hasFutureCustomerBlocked = true
    }
  }

  const rawBlocked = hasDetailsBlocked
    ? detailsBlocked
    : hasFutureCustomerBlocked
      ? options?.blockedUntil
      : undefined

  const formattedDate =
    typeof rawBlocked === 'number'
      ? toLocalIsoDate(rawBlocked)
      : rawBlocked != null
        ? String(rawBlocked)
        : ''

  const msg =
    typeof err === 'object' && err && 'message' in err
      ? String((err as { message: unknown }).message)
      : ''

  switch (code) {
    case 'failed-precondition': {
      if (options?.action === 'checkIn') {
        const lower = msg.toLowerCase()
        if (lower.includes('window') || lower.includes('early') || lower.includes('late') || lower.includes('outside')) {
          return t('NLSMemberBookingsScanErrorWindow')
        }
        if (lower.includes('not linked') || lower.includes('caller is not linked')) {
          return t('NLSMemberBookingsScanErrorNotLinked')
        }
        if (lower.includes('no booking') || lower.includes('no active booking')) {
          return t('NLSMemberBookingsScanErrorNoBooking')
        }
      }
      if (hasDetailsBlocked || hasFutureCustomerBlocked) {
        return t('NLSMemberBookingsErrorBlockedByStrikes', { date: formattedDate })
      }
      return t('NLSMemberBookingsErrorFailedPrecondition')
    }

    case 'not-found': {
      if (options?.action === 'link') {
        return t('NLSMemberBookingsNoPhoneRecord', {
          workspace: options.workspaceName || '',
        })
      }
      if (options?.action === 'checkIn') {
        return t('NLSMemberBookingsScanErrorNotLinked')
      }
      return t('NLSMemberBookingsErrorNotFound')
    }

    case 'resource-exhausted':
      return t('NLSMemberBookingsErrorResourceExhausted')

    case 'permission-denied':
      return t('NLSMemberBookingsErrorPermissionDenied')

    case 'unauthenticated':
      return t('NLSMemberBookingsErrorUnauthenticated')

    case 'deadline-exceeded':
      return t('NLSMemberBookingsErrorDeadlineExceeded')

    default:
      return t('NLSMemberBookingsErrorGeneric')
  }
}
